// src/pages/health/WorkoutDayDetail.js
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import AuthCard from "../../components/auth/AuthCard";
import BottomNav from "../../components/common/BottomNav";
import { useLoadingGate } from "../../hooks/useLoadingGate";
import legextension2 from "../../assets/images/legextension2.png";
import refresh from "../../assets/images/refresh.png";
import MyResultsModal from "./components/MyResultsModal";
import { TITLE_TO_KEY, VIDEOS_BY_ITEM } from "../../data/videos/todayVideos";
import { PlayCircleIcon } from "lucide-react";

function ArrowRightIcon({ className = "w-7 h-7" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path
                d="M5 12h12M13 5l7 7-7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/* ───────────────────────── 유틸 ───────────────────────── */
// 지금은 d일 지났어요 안 쓸 거지만, 혹시 다른 데 쓸 수 있으니 그냥 둬도 됨
const msDay = 24 * 60 * 60 * 1000;
const daysBetween = (a, b) => Math.floor((a - b) / msDay);

/* ───────────────────────── 메인 컴포넌트 ───────────────────────── */
export default function Workout() {
    const ready = useLoadingGate();
    const [sp] = useSearchParams();
    const [isResultsOpen, setIsResultsOpen] = useState(false);
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isDiseaseOpen, setIsDiseaseOpen] = useState(false);

    const navigate = useNavigate();

    const getKoreanName = (rawTitle) => {
        if (!rawTitle) return "";

        // 1) "(" 기준으로 앞부분만
        let t = rawTitle.split("(")[0].trim();

        // 2) 끝에 붙은 불필요한 기호 정리 (선택사항)
        // 예: "경추굴곡근 스트레치 – 네 발 ... " 뒤에 공백/대시만 남았을 때 제거
        t = t.replace(/[-–—\s]+$/u, "").trim();

        return t;
    };

    const RESULT_OPTIONS = [
        { key: "cardio_endurance", label: "심폐지구력" },
        { key: "triple_combo", label: "3종 복합 운동" },
        { key: "strength", label: "근력" },
        { key: "power", label: "근파워" },
        { key: "muscular_endurance", label: "근지구력" },
    ];

    const GUIDE_OPTIONS = [
        { key: "machine", label: "웨이트 기구 운동" },
        { key: "fitness", label: "피트니스 운동" },
    ];

    const DISEASE_GUIDE_OPTIONS = [
        { key: "metabolic", label: "대사 질환 가이드" },
        { key: "msk", label: "근골격계 운동 가이드" },
        { key: "pain", label: "통증별 운동 가이드" },
    ];

    const dateStr = sp.get("date") || format(new Date(), "yyyy-MM-dd");
    const dateObj = useMemo(() => {
        const d = parseISO(dateStr);
        return isValid(d) ? d : new Date();
    }, [dateStr]);

    // ★ 오늘의 운동 리스트: 이제 더미가 아니라 VIDEOS_BY_ITEM에서 가져옴
    const [exList, setExList] = useState([]);
    const [category, setCategory] = useState("상체"); // "상체" / "복부" / "하체"

    const currentGroupKey = useMemo(() => {
        const titleLabel = `${category} 운동`; // 예: "상체 운동"
        return TITLE_TO_KEY[titleLabel]; // 예: "upper-body"
    }, [category]);

    // ★ 특정 카테고리에서 랜덤으로 N개 뽑기
    const pickRandomVideos = (groupKey, count = 4) => {
        if (!groupKey) return [];
        const group = VIDEOS_BY_ITEM[groupKey];
        if (!Array.isArray(group) || group.length === 0) return [];

        if (group.length <= count) return group;

        const arr = [...group];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr.slice(0, count);
    };

    // ★ 카테고리나 키가 바뀔 때마다 추천 운동 새로 뽑기
    useEffect(() => {
        setExList(pickRandomVideos(currentGroupKey));
    }, [currentGroupKey]);

    // 새로고침 버튼 → 현재 카테고리에서 다시 랜덤 뽑기
    const handleRefresh = () => {
        setExList(pickRandomVideos(currentGroupKey));
    };

    const catBtnBase =
        "px-4 py-3 md:px-5 md:py-3.5 rounded-xl font-semibold transition shadow-sm";
    const catBtnActive = "bg-blue-500 text-white";
    const catBtnGhost = "bg-gray-100 text-gray-800 hover:bg-gray-200";

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={"운동"}>
                    <div className="flex flex-col flex-1 pt-3 md:pt-4 pb-24 md:pb-28">
                        {/* 추천 섹션 */}
                        <section className="rounded-2xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between px-5 md:px-6 py-4 md:py-5">
                                <p className="text-lg md:text-2xl font-extrabold tracking-tight">
                                    오늘은{" "}
                                    <span className="text-blue-500">이 운동</span> 어떠신가요?
                                </p>
                                <button onClick={handleRefresh} aria-label="새로고침">
                                    <img
                                        src={refresh}
                                        className="w-6 h-6 md:w-7 md:h-7 text-gray-600 object-contain"
                                        alt="새로고침"
                                    />
                                </button>
                            </div>

                            <div className="px-3 md:px-4">
                                <div className="h-px bg-gray-100" />
                            </div>

                            <ul className="space-y-3 px-1 py-2 md:py-3">
                                {exList.map((it, idx) => {
                                    const rawName = it.title || it.name || `운동 ${idx + 1}`;
                                    const name = getKoreanName(rawName);
                                    const thumb = it.thumb || it.thumbnail || legextension2;
                                    const hasVideo = !!it.id && !!currentGroupKey;

                                    const handleClick = () => {
                                        if (!hasVideo) return;
                                        const qs = new URLSearchParams({
                                            v: it.id,
                                            item: currentGroupKey,
                                            cat: "today",
                                        }).toString();
                                        navigate(`/workout/video/details?${qs}`);
                                    };
                                    return (
                                        <li key={idx}>
                                            <button
                                                type="button"
                                                onClick={handleClick}
                                                disabled={!hasVideo}
                                                // 1. border 클래스 제거 (기본 테두리 삭제)
                                                // 2. border-gray-100, hover:border-blue-200 제거 (색상 삭제)
                                                className={`
                                                    group relative flex items-center w-full p-3 md:p-4 rounded-2xl transition-all duration-200 text-left
                                                    ${hasVideo
                                                        ? "bg-white  hover:bg-blue-50/30 cursor-pointer"
                                                        : "bg-gray-50 opacity-60 cursor-default"
                                                    }
                                                `}
                                            >
                                                {/* 썸네일 영역 */}
                                                <div className="relative shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden shadow-sm bg-gray-200">
                                                    <img
                                                        src={thumb}
                                                        alt=""
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                    />

                                                </div>

                                                {/* 텍스트 영역 */}
                                                {/* 텍스트 영역 */}
                                                <div className="flex-1 min-w-0 ml-4 md:ml-5 flex flex-col justify-center h-full">
                                                    {hasVideo && (
                                                        <span className="inline-block px-2 py-0.5 mb-1.5 text-[10px] md:text-xs font-bold text-blue-600 bg-blue-100 rounded-md w-fit">
                                                            {category} 운동
                                                        </span>
                                                    )}

                                                    <h3
                                                        className={
                                                            "text-[17px] md:text-[19px] font-bold leading-tight truncate " +
                                                            (hasVideo
                                                                ? "text-gray-800 group-hover:text-blue-700"
                                                                : "text-gray-400")
                                                        }
                                                    >
                                                        {name}
                                                    </h3>

                                                    <p className="mt-1.5 text-xs md:text-sm font-medium text-gray-500 line-clamp-1">
                                                        {hasVideo
                                                            ? "오늘 이 운동에 한 번 도전해 보세요"
                                                            : "영상 준비 중입니다"}
                                                    </p>
                                                </div>

                                            </button>
                                        </li>
                                    );
                                })}

                                {/* 데이터가 없을 때 */}
                                {exList.length === 0 && (
                                    <li className="flex flex-col items-center justify-center py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-2xl mb-3">
                                            🧘
                                        </div>
                                        <p className="text-gray-500 font-medium">
                                            아직 등록된 <span className="font-bold text-gray-700">{category}</span> 운동 영상이 없어요.
                                        </p>
                                    </li>
                                )}
                            </ul>


                            <div className="px-3 md:px-4">
                                <div className="h-px bg-gray-100" />
                            </div>

                            <div className="grid grid-cols-3 gap-3 md:gap-4 px-3 md:px-4 py-5 md:py-6">
                                {["상체", "복부", "하체"].map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setCategory(c)}
                                        className={`w-full ${catBtnBase} ${category === c ? catBtnActive : catBtnGhost
                                            }`}
                                    >
                                        {c} 운동
                                    </button>
                                ))}
                            </div>
                        </section>

                        {/* 액션 카드 3개 */}
                        <section className="grid grid-cols-3 gap-3 md:gap-4 mt-5 md:mt-6">
                            <button
                                className="flex flex-col rounded-2xl border-2 border-blue-300 bg-blue-50 p-3 md:p-4 min-h-[180px] md:min-h-[200px] shadow-sm hover:shadow transition text-left"
                                onClick={() => setIsResultsOpen(true)}
                            >
                                <p className="text-[22px] md:text-[24px] font-extrabold leading-tight text-blue-700">
                                    나의
                                    <br />
                                    운동결과
                                </p>
                                <div className="mt-auto ml-auto w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-blue-300 grid place-items-center">
                                    <ArrowRightIcon className="w-7 h-7 md:w-8 md:h-8 text-blue-700" />
                                </div>
                            </button>

                            <button
                                className="flex flex-col rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-3 md:p-4 min-h-[180px] md:min-h-[200px] shadow-sm hover:shadow transition text-left"
                                onClick={() => setIsGuideOpen(true)}
                            >
                                <p className="text-[22px] md:text-[24px] font-extrabold leading-tight text-emerald-700">
                                    운동
                                    <br />
                                    가이드
                                </p>
                                <div className="mt-auto ml-auto w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-emerald-300 grid place-items-center">
                                    <ArrowRightIcon className="w-7 h-7 md:w-8 md:h-8 text-emerald-700" />
                                </div>
                            </button>

                            <button
                                className="flex flex-col rounded-2xl border-2 border-rose-300 bg-rose-50 p-3 md:p-4 min-h-[180px] md:min-h-[200px] shadow-sm hover:shadow transition text-left"
                                onClick={() => setIsDiseaseOpen(true)}
                            >
                                <p className="text-[22px] md:text-[24px] font-extrabold leading-tight text-rose-700">
                                    질환별
                                    <br />
                                    가이드
                                </p>
                                <div className="mt-auto ml-auto w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-rose-300 grid place-items-center">
                                    <ArrowRightIcon className="w-7 h-7 md:w-8 md:h-8 text-rose-700" />
                                </div>
                            </button>
                        </section>
                    </div>
                </AuthCard>
            )}
            <MyResultsModal
                open={isResultsOpen}
                onClose={() => setIsResultsOpen(false)}
                title="어떤 운동 결과가 필요하신가요?"
                options={RESULT_OPTIONS}
                onSelect={(opt) => {
                    navigate(`/workout/${opt.key}`);
                    setIsResultsOpen(false);
                }}
            />

            <MyResultsModal
                open={isGuideOpen}
                onClose={() => setIsGuideOpen(false)}
                title="어떤 운동 가이드가 필요하신가요?"
                options={GUIDE_OPTIONS}
                onSelect={(opt) => {
                    navigate(`/workout/workoutguide?category=${opt.key}`);
                    setIsGuideOpen(false);
                }}
            />

            <MyResultsModal
                open={isDiseaseOpen}
                onClose={() => setIsDiseaseOpen(false)}
                title="어떤 가이드가 필요하신가요?"
                options={DISEASE_GUIDE_OPTIONS}
                onSelect={(opt) => {
                    navigate(`/workout/conditionguide/${opt.key}`);
                    setIsDiseaseOpen(false);
                }}
            />

            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
